import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap } from "../client";
import { noErrorToast } from "../mutationMeta";
import type { Tour } from "../types";

const keys = {
  tours: (slug: string) => ["tenant", slug, "tours"] as const,
  notices: (slug: string, unread: boolean) => ["tenant", slug, "notices", unread] as const,
};

export function useTours(slug: string) {
  return useQuery({
    queryKey: keys.tours(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/tours/", { params: { path: { tenant_slug: slug } } })),
    staleTime: 5 * 60_000,
  });
}

export function useTourProgress(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast, // background progress; the tour or notice simply shows again
    retry: 1, // a lost "skip" would bring the tour back
    mutationFn: ({ key, action }: { key: string; action: "start" | "complete" | "dismiss" }) =>
      unwrap(
        api.POST("/api/t/{tenant_slug}/tours/{tour_key}/progress/", {
          params: { path: { tenant_slug: slug, tour_key: key } },
          body: { action },
        }),
      ),
    onSuccess: (tour: Tour) => {
      qc.setQueryData<Tour[]>(keys.tours(slug), (old) => old?.map((t) => (t.key === tour.key ? tour : t)));
    },
  });
}

export function useNotices(slug: string, unread: boolean, enabled = true) {
  return useQuery({
    queryKey: keys.notices(slug, unread),
    enabled,
    queryFn: () =>
      unwrap(
        api.GET("/api/t/{tenant_slug}/notices/", {
          params: { path: { tenant_slug: slug }, query: unread ? { unread: true } : {} },
        }),
      ),
    staleTime: 5 * 60_000,
  });
}

export function useAcknowledgeNotices(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast, // background progress; the tour or notice simply shows again
    retry: 1,
    mutationFn: (keys_: string[]) =>
      unwrap(api.POST("/api/t/{tenant_slug}/notices/acknowledge/", { params: { path: { tenant_slug: slug } }, body: { keys: keys_ } })),
    onSettled: () => qc.invalidateQueries({ queryKey: ["tenant", slug, "notices"] }),
  });
}
